import React, { useContext } from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import { ThemeContext } from "../../../../context/ThemeContext";

// --- utils ---
function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) { const t = a % b; a = b; b = t; } return a || 1; }
function simplify(p, q) { if (q < 0) { p = -p; q = -q; } const g = gcd(p, q); return [p / g, q / g]; }
function dfrac(p, q) { const [pp, qq] = simplify(p, q); return qq === 1 ? `${pp}` : `\\dfrac{${pp}}{${qq}}`; }
function parseFraction(raw) {
  const s = String(raw).replace(",", ".").trim();
  if (/^[+-]?\d+$/.test(s)) return [parseInt(s, 10), 1];
  const m = s.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/);
  if (!m || parseInt(m[2], 10) === 0) return null;
  return [parseInt(m[1], 10), parseInt(m[2], 10)];
}

function kulePhrase(n, color) {
  if (n === 1) return `${n} ${color} kula`;
  const twoToFour = (n % 10 >= 2 && n % 10 <= 4 && (n < 10 || n > 20));
  if (twoToFour) {
    const adj = color === "biała" ? "białe" : "czarne";
    return `${n} ${adj} kule`;
  }
  const adjGen = color === "biała" ? "białych" : "czarnych";
  return `${n} ${adjGen} kul`;
}

const MODES = [
  { key: "W", title: "biała",  fav: ({ W }) => W },
  { key: "B", title: "czarna", fav: ({ B }) => B },
];

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

function BallsBar({ W, B }) {
  const { theme } = useContext(ThemeContext);
  const border = theme === "light" ? "#000" : "#fff";
  const whiteFill = theme === "light" ? "#fff" : "#ddd";
  const blackFill = theme === "light" ? "#111" : "#000";
  const cell = { width: 18, height: 18, borderRadius: "50%", margin: "2px", border: `1px solid ${border}` };

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, margin: "8px 0" }}>
      {Array.from({ length: W }).map((_, i) => <div key={`w${i}`} style={{ ...cell, background: whiteFill }} />)}
      {Array.from({ length: B }).map((_, i) => <div key={`b${i}`} style={{ ...cell, background: blackFill }} />)}
    </div>
  );
}

function generateOne() {
  const W = 2 + Math.floor(Math.random() * 5); // 2..6
  const B = 2 + Math.floor(Math.random() * 5); // 2..6
  const N = W + B;
  const mode = pick(MODES);

  const omega = N;
  const fav = mode.fav({ W, B });
  const [p, q] = simplify(fav, omega);

  return { W, B, N, omega, fav, p, q, modeKey: mode.key, modeTitle: mode.title }; // czyste
}

export function generateTask() {
  const d = generateOne();
  return {
    type: "UrnOneDrawTask",
    data: d,
    uid: `urn1_${d.modeKey}_${d.W}_${d.B}_${Date.now()}_${Math.floor(Math.random() * 1e9)}`
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", margin: "12px 0" }}>
      <div>
        W urnie znajduje się {kulePhrase(data.W, "biała")} i {kulePhrase(data.B, "czarna")}.
        Losujemy jedną kulę. Oblicz prawdopodobieństwo, że wylosowana kula będzie: <strong>{data.modeTitle}</strong>.
      </div>
      <BallsBar W={data.W} B={data.B} />
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-3">
      <span><InlineMath math={'P(A) = '} /></span>
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label="P(A)"
        placeholder="np. 3/7"
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return { correct: false, message: "Podaj ułamek (np. 3/7) lub liczbę." };
  }
  const parsed = parseFraction(userAnswer.raw);
  if (!parsed) return { correct: false, message: "Nieprawidłowy ułamek." };

  const [gp, gq] = parsed;
  const [cp, cq] = simplify(data.p, data.q);
  const [gpp, gqq] = simplify(gp, gq);
  const correct = (gpp === cp && gqq === cq);
  return { correct, expected: `\\tfrac{${cp}}{${cq}}`, needsSimplification: !correct && (gp / gq === cp / cq) };
}

export function renderAnswer({ data }) {
  return (
    <div style={{ marginTop: "0.5rem" }}>
      <InlineMath math={`P(A) = ${dfrac(data.p, data.q)}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ marginTop: "0.5rem" }}>
      <InlineMath math={formatLatexPreview(userAnswer.raw, 'P(A)')} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const { N, omega, fav, modeTitle } = data;
  const [simpNum, simpDen] = simplify(fav, omega);
  const tail = (simpNum === fav && simpDen === omega) ? "" : `= \\dfrac{${simpNum}}{${simpDen}}`;

  const omegaLine = `\\Omega = \\{\\text{wszystkie } ${N} \\text{ kule}\\},\\; |\\Omega| = ${omega}`;
  const aCountLine = `|\\mathrm{A}| = ${fav}`;
  const probLine  = `P(\\mathrm{A}) = \\dfrac{|\\mathrm{A}|}{|\\Omega|} = \\dfrac{${fav}}{${omega}} ${tail}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <div style={{ marginBottom: 8 }}>
        <strong>Krok 1 — przestrzeń zdarzeń</strong>
        <BlockMath math={omegaLine} />
      </div>
      <div style={{ marginBottom: 8 }}>
        <strong>Krok 2 — zdarzenie A</strong>
        <div><em>Zdarzenie:</em> wylosowana kula jest <b>{modeTitle}</b></div>
        <BlockMath math={aCountLine} />
      </div>
      <div>
        <strong>Krok 3 — prawdopodobieństwo</strong>
        <BlockMath math={probLine} />
      </div>
    </div>
  );
}
